import { XIColors } from '@inmoment/the-kitchen';
import { motion } from 'framer-motion';
import * as React from 'react';
import { TextAccordionCategory, TextAccordionPage, TextAccordionPageSection } from './TextAccordion.model';
import './TextAccordion.css';

const recursiveSearch = (item: any[], key: string, value: string): any | undefined => {
  let val: any = undefined;
  item.forEach((itemVal) => {
    if (itemVal[key] === value) {
      val = itemVal;
    } else {
      if (itemVal['items']?.length) {
        const newVal = recursiveSearch(itemVal['items'], key, value);
        if (newVal) {
          val = newVal;
        }
      }
      if (itemVal['sections']?.length) {
        const newVal = recursiveSearch(itemVal['sections'], key, value);
        if (newVal) {
          val = newVal;
        }
      }
    }
  });
  return val as string | undefined;
};

const determineTopIndicator = (item: TextAccordionPage, activeId: string): number => {
  if (!item.sections) {
    return 0;
  }
  for (let i = 0; i < item.sections.length; i++) {
    if (item.sections[i].id === activeId) {
      return i * 24;
    }
  }

  return 0;
};

interface MenuContextProps {
  onItemClick: (item: TextAccordionPageSection | TextAccordionPage) => void;
  activeId: string;
  setActiveId: React.Dispatch<React.SetStateAction<string>>;
  activeCategoryId: string | undefined;
  setActiveCategoryId: React.Dispatch<React.SetStateAction<string | undefined>>;
}
const MenuContext = React.createContext<MenuContextProps>({
  onItemClick: () => {},
  activeId: '',
  setActiveId: () => {},
  activeCategoryId: undefined,
  setActiveCategoryId: () => {},
});

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data: TextAccordionCategory[];
  activeLink?: string;
  location: string;
  onItemClick: (item: TextAccordionPageSection | TextAccordionPage) => void;
}

/**
 * Description: Expanding side navigation for more complex, nested items;
 */
export const TextAccordion = (props: Props) => {
  const { data, className, activeLink, location, onItemClick, ...ingredients } = props;
  const [activeId, setActiveId] = React.useState<string>(activeLink || '');
  const [activeCategoryId, setActiveCategoryId] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (window.location.href.split('/')?.splice(-1)?.[0]?.split('#')[1] && !location?.split('#')[1]) {
      const newLocation = window.location.href.split('/')?.splice(-1)?.[0];
      setActiveId(recursiveSearch(data, 'href', `/${newLocation}`)?.id);
      setActiveCategoryId('');
    } else {
      const newValue = recursiveSearch(data, 'href', location);
      setActiveCategoryId(newValue?.id || '');
      setActiveId(newValue?.containsIds?.[0]);
    }
  }, [data, location]);

  const menuContext = React.useMemo<MenuContextProps>(
    () => ({
      activeId,
      setActiveId,
      activeCategoryId,
      setActiveCategoryId,
      onItemClick,
    }),
    [activeCategoryId, activeId, onItemClick],
  );

  return (
    <div className={`${className || ''} text-accordion-wrapper`} {...ingredients}>
      {data.map((category: TextAccordionCategory) => (
        <MenuContext.Provider key={category.id} value={menuContext}>
          <CategoryMenu category={category} />
        </MenuContext.Provider>
      ))}
    </div>
  );
};
TextAccordion.displayName = 'TextAccordion';

const CategoryMenu = (props: { category: TextAccordionCategory }) => {
  const { category } = props;
  return (
    <div style={{ marginBottom: 24 }}>
      <p className="kxi-text-300">{category.title}</p>
      <hr />
      {category.items.map((item, i) => (
        <CategoryItem key={item.id ?? i} item={item} />
      ))}
    </div>
  );
};
CategoryMenu.displayName = 'CategoryMenu';

const CategoryItem = (props: { item: TextAccordionCategory['items'][number] }) => {
  const { item } = props;
  return 'items' in item ? <CategoryMenu category={item} /> : <PageMenu page={item} />;
};
CategoryItem.displayName = 'CategoryItem';

const PageMenu = (props: { page: TextAccordionPage }) => {
  const { page } = props;
  const { activeId, setActiveId, activeCategoryId, onItemClick } = React.useContext(MenuContext);
  const isActive = page.containsIds?.includes(activeId) ?? false;

  return (
    <>
      <div
        className={`text-accordion-menu-button`}
        style={{ position: 'relative' }}
        onClick={() => {
          if (page.sections && page.sections.length > 0 && page.containsIds && !page.containsIds.includes(activeId)) {
            setActiveId(page.sections[0].id);
          }
          const body = document.querySelector('#route-wrapper');
          body?.scrollTo(0, 0);
          onItemClick?.(page);
        }}
      >
        {isActive || activeCategoryId === page.id ? <b>{page.title}</b> : <p>{page.title}</p>}
      </div>
      <motion.div
        style={{ overflow: 'hidden', position: 'relative' }}
        initial={false}
        transition={{ damping: 200, stiffness: 10 }}
        animate={{ height: page.containsIds && page.containsIds.includes(activeId) ? (page.sections !== undefined ? 'unset' : 0) : 0 }}
      >
        <motion.div
          style={{
            position: 'absolute',
            background: XIColors.orca.primary,
            width: 3,
            height: 24,
            left: 12,
            top: determineTopIndicator(page, activeId),
            display: page.containsIds && page.containsIds.includes(activeId) ? (page.sections !== undefined ? 'block' : 'none') : 'none',
          }}
          animate={{ top: determineTopIndicator(page, activeId) }}
          transition={{ duration: 0.125 }}
        />
        {page.sections?.map((section) => (
          <PageSection key={section.id} section={section} />
        ))}
      </motion.div>
    </>
  );
};
PageMenu.displayName = 'PageMenu';

const PageSection = (props: { section: TextAccordionPageSection }) => {
  const { section } = props;
  const { activeId, setActiveId, onItemClick } = React.useContext(MenuContext);
  const isActive = section.id === activeId;

  return (
    <div
      className={`text-accordion-menu-item-wrapper`}
      onClick={() => {
        setActiveId(section.id);
        if (onItemClick) onItemClick(section);
      }}
    >
      <div className={`text-accordion-menu-item`}>
        {isActive ? <b>{section.title}</b> : <p className={`text-accordion-menu-item-text`}>{section.title}</p>}
      </div>
    </div>
  );
};
PageSection.displayName = 'PageSection';
